import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, useLanguage } from '../context/AppContext';
import Layout from '../components/Layout';
import NextBadgeWidget from '../components/NextBadgeWidget';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Footprints, Loader2, Target, Trophy, ArrowLeft, Zap, Flame, CheckCircle2, Calendar, TrendingUp, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

function CircleGauge({ steps, goal }) {
  const size = 240;
  const stroke = 14;
  const pct = goal > 0 ? Math.min(100, (steps / goal) * 100) : 0;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const offset = c - (pct / 100) * c;
  const hit = steps >= goal;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="walk-grad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={hit ? '#F59E0B' : '#3B82F6'} />
            <stop offset="100%" stopColor={hit ? '#F97316' : '#6366F1'} />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" strokeWidth={stroke} className="text-white/5" />
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="url(#walk-grad)" strokeWidth={stroke} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={offset} className="transition-all duration-700" />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Footprints className={`w-6 h-6 mb-1 ${hit ? 'text-amber-400' : 'text-blue-400'}`} />
        <span className="text-4xl font-black tracking-tight">{steps.toLocaleString()}</span>
        <span className="text-[11px] text-muted-foreground uppercase tracking-widest mt-0.5">
          / {goal.toLocaleString()} pas
        </span>
      </div>
    </div>
  );
}

/**
 * 📊 World Class step histogram (Yuri's spec — 2026-05-05).
 *
 * Visual contract:
 *   • Hit goal → glowing ORANGE FLUO (drop-shadow glow, slight pulse)
 *   • Miss     → DARK GREY (no warmth, no false reward)
 *   • Goal line: dashed amber, "CIBLE : N" label pinned at the top right
 *   • Streak   → animated 🔥 in the top-left corner if 3+ consecutive hits
 *   • Momentum → "+X% vs sem. dern." chip above the chart with arrow + color
 *
 * Rationale: dopamine on hit, neutral on miss. Avoid blue-on-miss because
 * blue is too "rewarding" — Yuri wants to literally see grey when below goal.
 */
function BarChart({ bars, goal, label, average, formatLabel, deltaPct, showStreakFlame = false, language }) {
  const max = useMemo(() => Math.max(goal, ...bars.map(b => b.steps || 0), 1), [bars, goal]);
  const fr = (language || 'fr') === 'fr';

  // Dopamine-style streak: count current trailing streak in the bars themselves
  // (so the flame only appears when the LAST 3+ bars are at goal — i.e., the
  // user is currently riding a streak right now, not historically).
  // A bar is "hit" if it carries an explicit `hit` flag (used by the year
  // chart where "month at goal" is "≥20 daily-goal days") OR falls back to
  // `steps >= goal` for daily bars.
  const isHit = (b) => (typeof b.hit === 'boolean' ? b.hit : (b.steps || 0) >= goal);
  const trailingStreak = useMemo(() => {
    let n = 0;
    for (let i = bars.length - 1; i >= 0; i--) {
      if (isHit(bars[i])) n++;
      else break;
    }
    return n;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bars, goal]);
  const showFlame = showStreakFlame && trailingStreak >= 3;

  return (
    <div>
      {/* Header: label + momentum widget */}
      <div className="flex items-baseline justify-between mb-2 gap-2">
        <p className="text-[10px] text-muted-foreground uppercase tracking-widest">{label}</p>
        <div className="flex items-center gap-2">
          {typeof deltaPct === 'number' && (
            <DeltaChip deltaPct={deltaPct} fr={fr} />
          )}
          {typeof average === 'number' && average > 0 && (
            <p className="text-[10px] text-muted-foreground">
              <span className="uppercase tracking-widest mr-1">Moy.</span>
              <span className="font-bold text-foreground">{average.toLocaleString()}</span>
            </p>
          )}
        </div>
      </div>

      {/* Chart canvas — pad-top so the CIBLE label has room above the bars */}
      <div className="relative h-32 pt-4 flex items-end gap-1">
        {/* Streak flame badge — top-left corner, sits inside the padding */}
        {showFlame && (
          <div
            className="absolute top-0 left-0 flex items-center gap-1 px-2 py-0.5 rounded-full bg-gradient-to-r from-orange-600/90 to-amber-500/90 shadow-lg shadow-amber-500/40 z-20"
            data-testid="walking-streak-flame"
            style={{ animation: 'walking-flame-pulse 1.4s ease-in-out infinite' }}
          >
            <span className="text-sm leading-none">🔥</span>
            <span className="text-[10px] font-black text-white tracking-tight">
              {trailingStreak}{fr ? 'j' : 'd'}
            </span>
          </div>
        )}

        {bars.map((b, i) => {
          const h = (b.steps / max) * 100;
          const hit = b.steps >= goal;
          const short = formatLabel ? formatLabel(b) : (b.date || b.month || '').slice(-2);
          return (
            <div key={i} className="flex-1 flex flex-col items-end gap-1 min-w-0 h-full">
              <div className="relative w-full flex-1 flex items-end">
                <div
                  className={`w-full rounded-t transition-all duration-500 ${
                    hit
                      ? 'bg-gradient-to-t from-orange-600 via-orange-500 to-amber-300'
                      : 'bg-zinc-700/70'
                  }`}
                  style={{
                    height: `${Math.max(h, 2)}%`,
                    boxShadow: hit ? '0 0 14px rgba(251,146,60,0.55), 0 0 4px rgba(245,158,11,0.45)' : 'none',
                  }}
                  title={`${b.date || b.month}: ${b.steps.toLocaleString()}`}
                />
              </div>
              <span className={`text-[8px] ${hit ? 'text-amber-400 font-bold' : 'text-muted-foreground'}`}>
                {short}
              </span>
            </div>
          );
        })}

        {/* Goal line — dashed + clear "CIBLE" label */}
        <div
          className="absolute left-0 right-0 border-t-2 border-dashed border-amber-400/70 pointer-events-none"
          style={{ bottom: `calc(${(goal / max) * 100}% * (100% - 1.25rem) / 100% + 0.625rem)` }}
        >
          <span className="absolute right-0 -top-3.5 text-[9px] font-black tracking-wider text-amber-400 bg-card/90 px-1.5 py-0.5 rounded">
            {fr ? 'CIBLE' : 'GOAL'} : {goal.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Inline keyframes — kept local to avoid global CSS pollution */}
      <style>{`
        @keyframes walking-flame-pulse {
          0%, 100% { transform: scale(1); filter: drop-shadow(0 0 4px rgba(251,146,60,0.6)); }
          50% { transform: scale(1.08); filter: drop-shadow(0 0 10px rgba(251,146,60,0.95)); }
        }
      `}</style>
    </div>
  );
}

/**
 * Momentum chip — green/red/grey arrow with %.
 * Green ≥ +1%, red ≤ -1%, grey otherwise.
 */
function DeltaChip({ deltaPct, fr }) {
  const up = deltaPct >= 1;
  const down = deltaPct <= -1;
  const flat = !up && !down;
  const Icon = up ? ArrowUp : down ? ArrowDown : Minus;
  const palette = up
    ? 'bg-emerald-500/15 text-emerald-400 ring-1 ring-emerald-500/30'
    : down
      ? 'bg-red-500/15 text-red-400 ring-1 ring-red-500/30'
      : 'bg-muted/40 text-muted-foreground ring-1 ring-border/40';
  const sign = deltaPct > 0 ? '+' : '';
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${palette}`}
      data-testid="walking-week-delta"
      title={fr ? 'Comparaison avec la semaine précédente' : 'Compared to last week'}
    >
      <Icon className="w-3 h-3" />
      {sign}{deltaPct}% {fr ? (flat ? 'stable' : 'vs sem. dern.') : (flat ? 'flat' : 'vs last wk')}
    </span>
  );
}

export default function WalkingCommand() {
  const { language } = useLanguage();
  const fr = language === 'fr';
  const navigate = useNavigate();

  const [overview, setOverview] = useState(null);
  const [walkingCatalog, setWalkingCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inputVal, setInputVal] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingGoal, setEditingGoal] = useState(false);
  const [goalDraft, setGoalDraft] = useState(10000);
  // Retroactive date picker — null = today, otherwise YYYY-MM-DD (≤30 days back).
  const [targetDate, setTargetDate] = useState('');
  const [selectedBadge, setSelectedBadge] = useState(null);

  const rarityColor = (rarity) => ({
    legendary: 'from-yellow-500 to-amber-600',
    epic: 'from-purple-500 to-violet-600',
    rare: 'from-blue-500 to-indigo-600',
    uncommon: 'from-emerald-500 to-teal-600',
    common: 'from-slate-500 to-zinc-600',
  }[rarity] || 'from-slate-500 to-zinc-600');

  const formatProgress = (badge) => {
    const prog = (overview?.progress_by_badge || {})[badge.id];
    if (!prog) return null;
    const { current, target, unit } = prog;
    const pct = target > 0 ? Math.min(100, Math.round((current / target) * 100)) : 0;
    const unitLabelMap = {
      fr: {
        steps_today: 'pas aujourd\'hui',
        steps_sunday: 'pas dimanche',
        streak_days: 'jours consecutifs',
        goal_days_total: 'jours objectif atteints',
        lifetime_steps: 'pas cumules',
        steps_night: 'pas entre 20h-06h (voir le moteur)',
        steps_morning: 'pas avant 08h (voir le moteur)',
        goal_no_workout: 'objectif jour calme',
        session_timed: 'session chronometree (a venir)',
        weekly_rank: 'classement top 10% (a venir)',
      },
      en: {
        steps_today: 'steps today',
        steps_sunday: 'steps on Sunday',
        streak_days: 'consecutive days',
        goal_days_total: 'goal days total',
        lifetime_steps: 'lifetime steps',
        steps_night: 'steps 8pm-6am',
        steps_morning: 'steps before 8am',
        goal_no_workout: 'goal on rest day',
        session_timed: 'timed session (coming)',
        weekly_rank: 'top 10% rank (coming)',
      },
    }[fr ? 'fr' : 'en'];
    return {
      current,
      target,
      pct,
      label: unitLabelMap[unit] || unit,
      hiddenProgress: ['steps_night', 'steps_morning', 'goal_no_workout', 'session_timed', 'weekly_rank'].includes(unit),
    };
  };

  const fetchData = useCallback(async () => {
    try {
      const [ov, cat] = await Promise.all([
        api.get('/steps/overview'),
        api.get('/badges/catalog'),
      ]);
      setOverview(ov.data);
      const walking = (cat.data.badges || []).filter(b => b.section === 'marche');
      setWalkingCatalog(walking);
      setGoalDraft(ov.data?.today?.goal || 10000);
    } catch {
      toast.error(fr ? 'Erreur de chargement' : 'Load error');
    } finally {
      setLoading(false);
    }
  }, [fr]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Assistant IA deep-link: /walking?goal=12000
  useEffect(() => {
    const g = new URLSearchParams(window.location.search).get('goal');
    if (!g) return;
    const n = parseInt(g, 10);
    if (!isNaN(n) && n >= 1000 && n <= 50000) {
      setGoalDraft(n);
      setEditingGoal(true);
    }
    const next = new URLSearchParams(window.location.search);
    next.delete('goal');
    const newSearch = next.toString();
    window.history.replaceState({}, '', `${window.location.pathname}${newSearch ? `?${newSearch}` : ''}`);
  }, []);

  const logSteps = async () => {
    const val = parseInt(inputVal);
    if (isNaN(val) || val < 0) {
      toast.error(fr ? 'Nombre invalide' : 'Invalid number');
      return;
    }
    setSaving(true);
    try {
      const payload = { steps: val };
      if (targetDate) payload.date = targetDate;
      const res = await api.post('/steps/log', payload);
      const dayLabel = targetDate
        ? ` (${new Date(targetDate + 'T12:00:00').toLocaleDateString(fr ? 'fr-CA' : 'en-CA', { month: 'short', day: 'numeric' })})`
        : '';
      toast.success((fr ? `+${res.data.tactical_points || 0} pts tactiques` : `+${res.data.tactical_points || 0} tactical pts`) + dayLabel);
      (res.data.new_badges || []).forEach(b => {
        toast.success(`🏅 ${b.name_fr || b.name}`);
      });
      setInputVal('');
      setTargetDate('');
      await fetchData();
    } catch (err) {
      const msg = err?.response?.data?.detail
        || (fr ? 'Erreur' : 'Error');
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const saveGoal = async () => {
    if (goalDraft < 1000 || goalDraft > 100000) {
      toast.error(fr ? 'Objectif entre 1 000 et 100 000' : 'Goal between 1,000 and 100,000');
      return;
    }
    try {
      await api.put('/steps/goal', { goal: goalDraft });
      toast.success(fr ? `Objectif : ${goalDraft.toLocaleString()} pas` : `Goal: ${goalDraft.toLocaleString()} steps`);
      setEditingGoal(false);
      await fetchData();
    } catch {
      toast.error(fr ? 'Erreur' : 'Error');
    }
  };

  if (loading) {
    return <Layout><div className="flex items-center justify-center min-h-[60vh]"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  }

  const today = overview?.today || {};
  const life = overview?.lifetime || {};
  const week = overview?.week || [];
  const month = overview?.month || [];
  const year = overview?.year || null;
  const previousYears = overview?.previous_years || [];
  const weekAvg = overview?.week_average || 0;
  const monthAvg = overview?.month_average || 0;
  const weekDeltaPct = overview?.week_delta_pct;  // null if no prior week to compare
  const maxPastDays = overview?.retroactive_max_days || 30;
  const earnedIds = new Set((overview?.earned_walking_badges || []).map(b => b.badge_id));
  const goal = today.goal || 10000;

  // Date picker bounds — today back to 30 days ago.
  const todayIso = new Date().toISOString().slice(0, 10);
  const minDateIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() - maxPastDays);
    return d.toISOString().slice(0, 10);
  })();
  const MONTH_LABELS_FR = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
  const MONTH_LABELS_EN = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const formatMonthTick = (b) => {
    const m = parseInt((b.month || '').slice(5, 7), 10);
    if (!m) return '';
    return (fr ? MONTH_LABELS_FR : MONTH_LABELS_EN)[m - 1] || '';
  };

  return (
    <Layout>
      <div className="px-4 sm:px-6 py-6 max-w-3xl mx-auto space-y-5 pb-24">
        {/* Header */}
        <div className="flex items-center gap-3 mb-1">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="h-9 w-9">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight leading-none">
              {fr ? 'Centre de Commandement' : 'Command Center'}
            </h1>
            <p className="text-[10px] uppercase tracking-[0.2em] text-primary">{fr ? 'Marche' : 'Walking'}</p>
          </div>
        </div>

        {/* TOP — Jauge + Points Tactiques */}
        <Card className="border-border/50 bg-gradient-to-br from-card via-card to-card/50 overflow-hidden">
          <CardContent className="p-6 flex flex-col items-center gap-4">
            <CircleGauge steps={today.steps || 0} goal={goal} />
            <div className="flex items-center gap-6 pt-2">
              <div className="text-center">
                <div className="flex items-center gap-1.5 justify-center text-amber-400">
                  <Zap className="w-4 h-4" />
                  <span className="text-xl font-black">{today.tactical_points || 0}</span>
                </div>
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
                  {fr ? 'Pts Tactiques' : 'Tactical Pts'}
                </p>
              </div>
              <div className="w-px h-10 bg-border" />
              <div className="text-center">
                <div className="flex items-center gap-1.5 justify-center text-orange-400">
                  <Flame className="w-4 h-4" />
                  <span className="text-xl font-black">{life.streak_days || 0}</span>
                </div>
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
                  {fr ? 'Jours d\'affilee' : 'Streak'}
                </p>
              </div>
              <div className="w-px h-10 bg-border" />
              <div className="text-center">
                <div className="flex items-center gap-1.5 justify-center text-blue-400">
                  <Footprints className="w-4 h-4" />
                  <span className="text-xl font-black">{life.total_km || 0}</span>
                </div>
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground mt-0.5">
                  {fr ? 'km au total' : 'km total'}
                </p>
              </div>
            </div>

            {/* Input + optional retro date picker */}
            <div className="w-full flex flex-col gap-2 pt-4">
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  placeholder={fr ? 'Entrer mes pas' : 'Enter my steps'}
                  value={inputVal}
                  onChange={(e) => setInputVal(e.target.value)}
                  className="flex-1 h-11 text-center font-bold bg-secondary border-primary/40"
                  data-testid="walking-input"
                />
                <Button onClick={logSteps} disabled={saving || !inputVal} className="h-11 px-5 bg-primary text-primary-foreground" data-testid="walking-save-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (fr ? 'Enregistrer' : 'Save')}
                </Button>
              </div>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <Calendar className="w-3.5 h-3.5 shrink-0" />
                <span className="uppercase tracking-widest">{fr ? 'Jour' : 'Day'}</span>
                <Input
                  type="date"
                  value={targetDate}
                  min={minDateIso}
                  max={todayIso}
                  onChange={(e) => setTargetDate(e.target.value)}
                  className="h-8 text-xs bg-secondary/60 border-border/60 flex-1"
                  data-testid="walking-date-picker"
                />
                {targetDate && (
                  <button
                    type="button"
                    onClick={() => setTargetDate('')}
                    className="text-[10px] uppercase tracking-widest text-primary hover:text-primary/80"
                    data-testid="walking-date-reset"
                  >
                    {fr ? "Aujourd'hui" : 'Today'}
                  </button>
                )}
              </div>
              <p className="text-[10px] text-muted-foreground/80 italic">
                {targetDate
                  ? (fr
                    ? `Rattrapage : entrée enregistrée pour le ${new Date(targetDate + 'T12:00:00').toLocaleDateString('fr-CA', { weekday: 'long', day: 'numeric', month: 'long' })}`
                    : `Backfill: entry will be saved on ${new Date(targetDate + 'T12:00:00').toLocaleDateString('en-CA', { weekday: 'long', day: 'numeric', month: 'long' })}`)
                  : (fr ? `Tu peux rattraper jusqu'à ${maxPastDays} jours en arrière.` : `You can backfill up to ${maxPastDays} days.`)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Prochain Badge — carousel engagement */}
        <NextBadgeWidget />

        {/* MILIEU — Historique graphiques */}
        <Card className="border-border/50 bg-card/60">
          <CardContent className="p-5 space-y-5">
            <BarChart
              bars={week}
              goal={goal}
              average={weekAvg}
              label={fr ? 'Semaine (7 derniers jours)' : 'Week (last 7 days)'}
              deltaPct={typeof weekDeltaPct === 'number' ? weekDeltaPct : undefined}
              showStreakFlame
              language={language}
            />
            <div className="h-px bg-border/40" />
            <BarChart
              bars={month}
              goal={goal}
              average={monthAvg}
              label={fr ? 'Mois (30 derniers jours)' : 'Month (last 30 days)'}
              showStreakFlame
              language={language}
            />
            <div className="grid grid-cols-3 gap-2 pt-3">
              <div className="text-center bg-secondary/40 rounded-lg py-2">
                <p className="text-base font-black text-foreground">{(life.total_steps || 0).toLocaleString()}</p>
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{fr ? 'Pas totaux' : 'Total steps'}</p>
              </div>
              <div className="text-center bg-secondary/40 rounded-lg py-2">
                <p className="text-base font-black text-foreground">{life.total_goal_days || 0}</p>
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{fr ? 'Jours objectif' : 'Goal days'}</p>
              </div>
              <div className="text-center bg-secondary/40 rounded-lg py-2">
                <p className="text-base font-black text-foreground">{life.active_days || 0}</p>
                <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{fr ? 'Jours actifs' : 'Active days'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Année — 12 barres mensuelles + total annuel + podium */}
        {year && (
          <Card className="border-border/50 bg-card/60" data-testid="walking-year-card">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-primary" />
                <p className="font-heading uppercase text-sm tracking-wider">
                  {fr ? `Année ${year.year}` : `Year ${year.year}`}
                </p>
              </div>
              <BarChart
                bars={year.months}
                goal={(year.goal_daily || goal) * 30}
                average={year.avg_daily}
                label={fr ? 'Total par mois' : 'Total per month'}
                formatLabel={formatMonthTick}
                showStreakFlame
                language={language}
              />
              <div className="grid grid-cols-4 gap-2 pt-2">
                <div className="text-center bg-secondary/40 rounded-lg py-2">
                  <p className="text-sm font-black text-foreground">{(year.total_steps || 0).toLocaleString()}</p>
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{fr ? 'Total' : 'Total'}</p>
                </div>
                <div className="text-center bg-secondary/40 rounded-lg py-2">
                  <p className="text-sm font-black text-foreground">{(year.avg_daily || 0).toLocaleString()}</p>
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{fr ? 'Moy./jour' : 'Avg/day'}</p>
                </div>
                <div className="text-center bg-secondary/40 rounded-lg py-2">
                  <p className="text-sm font-black text-foreground">{year.days_at_goal || 0}</p>
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{fr ? 'Jours objectif' : 'Goal days'}</p>
                </div>
                <div className="text-center bg-secondary/40 rounded-lg py-2">
                  <p className="text-sm font-black text-foreground">{year.days_logged || 0}</p>
                  <p className="text-[9px] uppercase tracking-widest text-muted-foreground">{fr ? 'Actifs' : 'Active'}</p>
                </div>
              </div>

              {/* 🏆 Podium — top 3 months of the year */}
              {(year.podium || []).length > 0 && (
                <div className="pt-3 space-y-2" data-testid="walking-podium">
                  <div className="flex items-center gap-2">
                    <Trophy className="w-3.5 h-3.5 text-amber-400" />
                    <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
                      {fr ? `Mes meilleurs mois ${year.year}` : `My best months ${year.year}`}
                    </p>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {year.podium.map((p, i) => {
                      const rank = i + 1;
                      const palettes = [
                        { ring: 'ring-amber-400/50', bg: 'from-amber-500/20 to-amber-600/5', text: 'text-amber-300', medal: '🥇' },
                        { ring: 'ring-zinc-300/40', bg: 'from-zinc-400/15 to-zinc-500/5', text: 'text-zinc-200', medal: '🥈' },
                        { ring: 'ring-orange-700/40', bg: 'from-orange-700/15 to-orange-800/5', text: 'text-orange-300', medal: '🥉' },
                      ];
                      const pal = palettes[i] || palettes[2];
                      return (
                        <div
                          key={p.month}
                          className={`rounded-xl p-3 ring-1 ${pal.ring} bg-gradient-to-b ${pal.bg} text-center`}
                          data-testid={`walking-podium-${rank}`}
                        >
                          <div className="text-2xl leading-none mb-1">{pal.medal}</div>
                          <p className={`text-[10px] uppercase tracking-widest font-bold ${pal.text}`}>
                            {formatMonthTick(p)}
                          </p>
                          <p className="text-base font-black text-foreground mt-1">
                            {(p.steps || 0).toLocaleString()}
                          </p>
                          <p className="text-[9px] text-muted-foreground">
                            {p.days_at_goal || 0} {fr ? 'j. objectif' : 'goal d.'}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Comparaison années passées — visible seulement si l'utilisateur a au moins 1 année précédente. */}
        {previousYears.length > 0 && year && (() => {
          const rows = [
            { year: year.year, total_steps: year.total_steps, avg_daily: year.avg_daily, days_logged: year.days_logged, days_at_goal: year.days_at_goal, current: true },
            ...previousYears,
          ];
          const maxTotal = Math.max(...rows.map(r => r.total_steps || 0), 1);
          return (
            <Card className="border-border/50 bg-card/60" data-testid="walking-yoy-card">
              <CardContent className="p-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Trophy className="w-4 h-4 text-amber-400" />
                  <p className="font-heading uppercase text-sm tracking-wider">
                    {fr ? 'Comparaison des années' : 'Year-over-year'}
                  </p>
                </div>
                <div className="space-y-2">
                  {rows.map((r) => {
                    const pct = Math.round((r.total_steps / maxTotal) * 100);
                    return (
                      <div key={r.year} className={`rounded-lg p-3 ${r.current ? 'bg-primary/10 ring-1 ring-primary/30' : 'bg-secondary/40'}`} data-testid={`walking-yoy-${r.year}`}>
                        <div className="flex items-baseline justify-between mb-1.5">
                          <span className="font-heading text-base tracking-tight">
                            {r.year}
                            {r.current && <span className="ml-2 text-[9px] uppercase tracking-widest text-primary">{fr ? 'en cours' : 'current'}</span>}
                          </span>
                          <span className="text-sm font-black text-foreground">{(r.total_steps || 0).toLocaleString()}</span>
                        </div>
                        <div className="w-full h-1.5 rounded-full bg-muted/30 overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${r.current ? 'bg-gradient-to-r from-amber-500 to-orange-400' : 'bg-gradient-to-r from-blue-500/70 to-indigo-500/70'}`}
                            style={{ width: `${Math.max(pct, 2)}%` }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-[10px] text-muted-foreground mt-1.5">
                          <span>{fr ? 'Moy.' : 'Avg'} {(r.avg_daily || 0).toLocaleString()} / {fr ? 'jour' : 'day'}</span>
                          <span>{r.days_at_goal || 0} {fr ? 'jours objectif' : 'goal days'} · {r.days_logged || 0} {fr ? 'actifs' : 'active'}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* BAS — Objectif */}
        <Card className="border-border/50 bg-card/60">
          <CardContent className="p-5 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Target className="w-4 h-4 text-primary" />
                <p className="font-heading uppercase text-sm tracking-wider">{fr ? 'Mon objectif quotidien' : 'My daily goal'}</p>
              </div>
              {!editingGoal && (
                <Button variant="ghost" size="sm" onClick={() => setEditingGoal(true)} data-testid="edit-goal-btn" className="text-xs uppercase tracking-wider">
                  {fr ? 'Modifier' : 'Edit'}
                </Button>
              )}
            </div>
            {editingGoal ? (
              <div className="flex items-center gap-2">
                <Input type="number" min="1000" max="100000" step="500" value={goalDraft}
                  onChange={(e) => setGoalDraft(parseInt(e.target.value) || 0)}
                  className="flex-1 h-10 text-center font-bold bg-secondary border-primary/40"
                  data-testid="goal-input" />
                <Button onClick={saveGoal} className="bg-primary text-primary-foreground" data-testid="save-goal-btn">{fr ? 'OK' : 'OK'}</Button>
                <Button variant="ghost" onClick={() => { setEditingGoal(false); setGoalDraft(goal); }}>{fr ? 'Annuler' : 'Cancel'}</Button>
              </div>
            ) : (
              <p className="text-3xl font-black text-foreground">{goal.toLocaleString()} <span className="text-sm font-normal text-muted-foreground">{fr ? 'pas/jour' : 'steps/day'}</span></p>
            )}
          </CardContent>
        </Card>

        {/* BAS — Badges de marche */}
        <Card className="border-border/50 bg-card/60">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-400" />
                <p className="font-heading uppercase text-sm tracking-wider">{fr ? 'Badges de Marche' : 'Walking Badges'}</p>
              </div>
              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-primary/20 text-primary font-bold">
                {earnedIds.size} / {walkingCatalog.length}
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2">
              {walkingCatalog.map(b => {
                const earned = earnedIds.has(b.id);
                return (
                  <motion.button
                    key={b.id}
                    type="button"
                    whileHover={{ scale: earned ? 1.05 : 1.02 }}
                    whileTap={{ scale: 0.96 }}
                    onClick={() => setSelectedBadge(b)}
                    className={`relative aspect-square rounded-xl border-2 flex flex-col items-center justify-center p-2 transition-all cursor-pointer ${
                      earned
                        ? 'border-primary/40 bg-gradient-to-br from-primary/10 to-transparent shadow-lg'
                        : 'border-border/30 bg-muted/20 opacity-50 grayscale hover:grayscale-0 hover:opacity-80'
                    }`}
                    data-testid={`walk-badge-${b.id}`}
                  >
                    <span className="text-2xl">{b.icon}</span>
                    <p className={`text-[9px] leading-tight text-center mt-1 font-medium line-clamp-2 ${earned ? 'text-foreground' : 'text-muted-foreground'}`}>
                      {b.name}
                    </p>
                    {earned && (
                      <div className="absolute -top-1 -right-1 bg-emerald-500 rounded-full w-4 h-4 flex items-center justify-center">
                        <CheckCircle2 className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </motion.button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Badge detail modal */}
        <Dialog open={!!selectedBadge} onOpenChange={(v) => !v && setSelectedBadge(null)}>
          <DialogContent className="bg-card border-border max-w-sm" data-testid="badge-detail-modal">
            {selectedBadge && (() => {
              const earned = earnedIds.has(selectedBadge.id);
              const prog = formatProgress(selectedBadge);
              return (
                <>
                  <DialogHeader>
                    <DialogTitle className="font-heading uppercase tracking-tight text-center text-base">
                      {earned ? (fr ? 'Trophee debloque' : 'Trophy unlocked') : (fr ? 'Trophee verrouille' : 'Locked trophy')}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="flex flex-col items-center gap-4 py-3">
                    <div className={`w-28 h-28 rounded-2xl bg-gradient-to-br ${rarityColor(selectedBadge.rarity)} flex items-center justify-center shadow-xl ${earned ? '' : 'grayscale opacity-60'}`}>
                      <span className="text-6xl drop-shadow-lg">{selectedBadge.icon}</span>
                    </div>
                    <div className="text-center">
                      <h3 className="font-heading text-xl uppercase tracking-tight text-foreground leading-tight">
                        {selectedBadge.name}
                      </h3>
                      <p className="text-[10px] uppercase tracking-widest text-muted-foreground mt-1">
                        {selectedBadge.rarity}
                      </p>
                    </div>
                    <p className="text-sm text-center text-foreground/80 px-2">
                      {selectedBadge.description}
                    </p>

                    {/* Progress */}
                    {prog && !prog.hiddenProgress && (
                      <div className="w-full space-y-1.5 pt-1">
                        <div className="flex items-center justify-between text-xs">
                          <span className="text-muted-foreground">{prog.label}</span>
                          <span className="font-bold text-foreground">
                            {prog.current.toLocaleString()} / {prog.target.toLocaleString()}
                          </span>
                        </div>
                        <div className="w-full h-2.5 rounded-full bg-muted/30 overflow-hidden">
                          <div
                            className={`h-full rounded-full bg-gradient-to-r ${earned ? 'from-emerald-500 to-emerald-400' : 'from-primary to-primary/70'} transition-all duration-500`}
                            style={{ width: `${earned ? 100 : prog.pct}%` }}
                          />
                        </div>
                        <p className="text-[10px] text-right text-muted-foreground">
                          {earned
                            ? (fr ? 'Complete a 100% !' : 'Completed at 100%!')
                            : `${prog.pct}% ${fr ? 'complete' : 'complete'}`}
                        </p>
                      </div>
                    )}

                    {prog && prog.hiddenProgress && !earned && (
                      <p className="text-[11px] text-muted-foreground/80 italic text-center px-3 bg-muted/20 rounded py-2">
                        {fr
                          ? `Defi tactique : ${prog.label}.`
                          : `Tactical challenge: ${prog.label}.`}
                      </p>
                    )}

                    {earned && (
                      <div className="flex items-center gap-2 text-emerald-400 text-sm font-bold">
                        <CheckCircle2 className="w-4 h-4" />
                        {fr ? 'Tu as conquis ce badge' : "You've earned this badge"}
                      </div>
                    )}
                  </div>
                </>
              );
            })()}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
